import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import inputMixin from 'design-system/mixins/input';
import { paragraphMixins } from 'design-system/mixins/text';

const FakeInput = styled.div<{ focused: boolean }>`
  ${inputMixin};
  height: 32px;
  overflow: hidden;
  padding: ${Spacing[1]} ${Spacing[2]} ${Spacing[1]} 0;
  color: ${({ focused }) => (focused ? Color.PrimaryBlack : Color.CoreSlate800)};
`;

const StyledInput = styled.input<{ focused: boolean }>`
  ${paragraphMixins.lg};
  color: ${({ focused }) => (focused ? Color.PrimaryBlack : Color.CoreSlate800)};
  text-align: right;
`;

interface ZoomInputProps {
  zoomPercentage: number;
  handleZoomPercentageChange: (zoomPercentage: number) => void;
}

const ZoomInput = ({
  zoomPercentage: inputZoomPercentage,
  handleZoomPercentageChange,
}: ZoomInputProps) => {
  const [zoomPercentage, setZoomPercentage] = useState<string>(inputZoomPercentage.toString());
  const [focused, setFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // If the initial zoom percentage changes, it means
    // it was forced from the state manager. Update it
    // here as well.
    if (inputZoomPercentage.toString() !== zoomPercentage) {
      setZoomPercentage(inputZoomPercentage.toString());
    }
    // This hook should only run on inputZoomPercentage change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputZoomPercentage]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      inputRef.current?.blur();
      return;
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setZoomPercentage(e.target.value);
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = () => {
    setFocused(true);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    const newZoomPercentage = e.target.value.replace(/\D+/g, '');
    let numberValue = parseInt(newZoomPercentage, 10);
    if (isNaN(numberValue)) {
      numberValue = inputZoomPercentage;
    }
    const stringValue = numberValue.toString();
    setZoomPercentage(stringValue);
    handleZoomPercentageChange(numberValue);
    setFocused(false);
  };

  return (
    <FakeInput focused={focused}>
      <StyledInput
        type='text'
        focused={focused}
        ref={inputRef}
        size={Math.max(2, zoomPercentage.toString().length)}
        maxLength={4}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        value={zoomPercentage}
      />
      %
    </FakeInput>
  );
};

export default ZoomInput;
